import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Types
import { useFormContext } from 'react-hook-form';
import { Button, Flex } from '@wegroup/design-system';

interface Props {
  handleYes: () => void;
  handleNo: () => void;
  yesText?: string;
  noText?: string;
  disabledYes?: boolean;
  disabledNo?: boolean;
  className?: string;
  isYesLoading?: boolean;
  defaultValue?: boolean;
}

enum LastClicked {
  NONE,
  NO,
  YES,
}

const YesNoQuestion: React.FC<React.PropsWithChildren<Props>> = ({
  handleYes,
  handleNo,
  yesText,
  noText,
  disabledYes,
  disabledNo,
  isYesLoading,
  className,
  defaultValue,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const [lastClicked, setLastClicked] = useState<LastClicked>(LastClicked.NONE);
  const formContext = useFormContext();

  const handleOnNoClick = () => {
    // Silent disable, look better visually
    if (formContext?.formState.isSubmitting) return;
    setLastClicked(LastClicked.NO);
    handleNo();
  };
  const handleOnYesClick = () => {
    // Silent disable, look better visually
    if (formContext?.formState.isSubmitting) return;
    setLastClicked(LastClicked.YES);
    handleYes();
  };

  const isLoadingNo =
    lastClicked === LastClicked.NO && formContext?.formState.isSubmitting;
  const isLoadingYes =
    (lastClicked === LastClicked.YES && formContext?.formState.isSubmitting) ||
    isYesLoading;

  return (
    <Flex
      data-test-id="ProdFac_YesNoQuestion"
      className={className}
      {...otherProps}
      flexGrow={1}
      gridGap="4"
      maxW={{ laptop: 'sm' }}
      mx={{ laptop: 'auto' }}
    >
      <Button
        minH={{ base: '12', laptop: '14' }}
        flexGrow={1}
        variant="outline"
        onClick={handleOnNoClick}
        data-test-id="ProdFac_YesNoQuestion_No"
        isLoading={isLoadingNo}
        isDisabled={disabledNo || isLoadingNo}
        borderRadius="full"
        borderWidth={defaultValue === false ? '2px' : undefined}
        bgColor={defaultValue === false ? 'primary.50' : undefined}
      >
        {noText || t(`No`)}
      </Button>
      <Button
        minH={{ base: '12', laptop: '14' }}
        flexGrow={1}
        variant="outline"
        onClick={handleOnYesClick}
        data-test-id="ProdFac_YesNoQuestion_Yes"
        isLoading={isLoadingYes}
        isDisabled={disabledYes || isLoadingYes}
        borderRadius="full"
        borderWidth={defaultValue ? '2px' : undefined}
        bgColor={defaultValue ? 'primary.50' : undefined}
      >
        {yesText || t(`Yes`)}
      </Button>
    </Flex>
  );
};

export default YesNoQuestion;
